import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiFetch } from '../../utils/apiInterceptor';
import { API_BASE_URL } from '../../utils/paths';

// Async thunks for API calls
export const fetchAllSpots = createAsyncThunk(
  'spots/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/spots`, {
        method: 'GET',
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const fetchSpot = createAsyncThunk(
  'spots/getDetails',
  async (spotId, { rejectWithValue, getState }) => {
    
    try {
      const response = await apiFetch(`${API_BASE_URL}/spots/${spotId}`, {
        method: 'GET',
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const createSpot = createAsyncThunk(
  'spots/create',
  async (spotData, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/spots/create`, {
        method: 'POST',
        body: JSON.stringify(spotData),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const updateSpot = createAsyncThunk(
  'spots/update',
  async ({ spotId, spotData }, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/spots/${spotId}/update`, {
        method: 'PUT',
        body: JSON.stringify(spotData),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const deleteSpot = createAsyncThunk(
  'spots/delete',
  async (spotId, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/spots/${spotId}/delete`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        return rejectWithValue(data);
      }

      return spotId; // Return the deleted spot ID
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

const savedLocationsSlice = createSlice({
  name: 'savedLocations',
  initialState: {
    spots: [],
    currentSpot: null,
    isLoading: false,
    errors: null,
  },
  reducers: {
    clearErrors: (state) => {
      state.errors = null;
    },
    clearCurrentSpot: (state) => {
      state.currentSpot = null;
    },
    setCurrentSpot: (state, action) => {
      state.currentSpot = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Spots
      .addCase(fetchAllSpots.pending, (state) => {
        state.isLoading = true;
        state.errors = null;
      })
      .addCase(fetchAllSpots.fulfilled, (state, action) => {
        state.isLoading = false;
        state.spots = action.payload;
        state.errors = null;
      })
      .addCase(fetchAllSpots.rejected, (state, action) => {
        state.isLoading = false;
        state.errors = action.payload?.errors || ['Failed to fetch spots'];
      })
      // Fetch Spot by ID
      .addCase(fetchSpot.pending, (state) => {
        state.isLoading = true;
        state.errors = null;
      })
      .addCase(fetchSpot.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSpot = action.payload;
        state.errors = null;
      })
      .addCase(fetchSpot.rejected, (state, action) => {
        state.isLoading = false;
        state.errors = action.payload?.errors || ['Failed to fetch spot details'];
      })
      // Create Spot
      .addCase(createSpot.pending, (state) => {
        state.isLoading = true;
        state.errors = null;
      })
      .addCase(createSpot.fulfilled, (state, action) => {
        state.isLoading = false;
        const newSpot = action.payload.spot || action.payload;
        state.spots.push(newSpot);
        state.currentSpot = newSpot;
        state.errors = null;
      })
      .addCase(createSpot.rejected, (state, action) => {
        state.isLoading = false;
        state.errors = action.payload?.errors || ['Failed to create spot'];
      })
      // Update Spot
      .addCase(updateSpot.pending, (state) => {
        state.isLoading = true;
        state.errors = null;
      })
      .addCase(updateSpot.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedSpot = action.payload;
        const index = state.spots.findIndex(spot => spot.id === updatedSpot.id);
        if (index !== -1) {
          state.spots[index] = updatedSpot;
        }
        state.currentSpot = updatedSpot;
        state.errors = null;
      })
      .addCase(updateSpot.rejected, (state, action) => {
        state.isLoading = false;
        state.errors = action.payload?.errors || ['Failed to update spot'];
      })
      // Delete Spot
      .addCase(deleteSpot.pending, (state) => {
        state.isLoading = true;
        state.errors = null;
      })
      .addCase(deleteSpot.fulfilled, (state, action) => {
        state.isLoading = false;
        const deletedSpotId = action.payload;
        state.spots = state.spots.filter(spot => spot.id !== deletedSpotId);
        // Clear current spot if it was the deleted one
        if (state.currentSpot?.id === deletedSpotId) {
          state.currentSpot = null;
        }
        state.errors = null;
      })
      .addCase(deleteSpot.rejected, (state, action) => {
        state.isLoading = false;
        state.errors = action.payload?.errors || ['Failed to delete spot'];
      });
  },
});

export const { clearError, clearCurrentSpot, setCurrentSpot } = savedLocationsSlice.actions;
export default savedLocationsSlice.reducer;